import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { fetcher } from '../../../services/fetcher';
import { useTheme } from '../../../contexts/ThemeContext';
import { Badge, Button, Card, DateInput, SearchIcon, Select } from '../../../components/ui';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { cn } from '../../../lib/utils';
import { AccountingReportShell } from './shared/AccountingReportShell';
import { FieldLabel } from './shared/reportUtils';
import { buildReportQuery, useAccountingReportFilters } from './shared/useAccountingReportFilters';

type CheckItem = {
  code: string;
  label: string;
  status: 'pass' | 'fail' | 'warn';
  count: number;
  detailUrl?: string;
};

type Response = {
  items: CheckItem[];
};

const statusVariant = (status: CheckItem['status']) => {
  if (status === 'pass') return 'success' as const;
  if (status === 'fail') return 'danger' as const;
  return 'warning' as const;
};

export const PeriodCloseChecklist: React.FC = () => {
  const { colors } = useTheme();
  const f = useAccountingReportFilters();

  const reportUrl =
    f.run
      ? buildReportQuery('/accounting/reports/period-close-checklist', {
          dateFrom: f.dateFrom,
          dateTo: f.dateTo,
        })
      : null;

  const { data, isLoading } = useSWR<Response>(reportUrl, fetcher);

  const toolbar = (
    <Card className="p-4 shrink-0 space-y-3">
      <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
        <div className="min-w-[13rem] max-w-xs">
          <FieldLabel>Accounting period</FieldLabel>
          <Select value={f.periodId} onChange={(e) => f.setPeriodId(e.target.value)} options={f.periodOptions} />
        </div>
        <div>
          <FieldLabel>Close period range</FieldLabel>
          <div className="flex items-center gap-2">
            <DateInput value={f.dateFrom} onChange={(e) => { f.setPeriodId(''); f.setDateFrom(e.target.value); f.setRun(false); }} showFormatHint={false} />
            <span className={cn('text-sm pb-0.5', colors.textMuted)}>to</span>
            <DateInput value={f.dateTo} onChange={(e) => { f.setPeriodId(''); f.setDateTo(e.target.value); f.setRun(false); }} showFormatHint={false} />
          </div>
        </div>
      </div>
      <div className={cn('flex items-center gap-2 pt-3 border-t', colors.border)}>
        <Button variant="default" onClick={() => f.setRun(true)} disabled={!f.canSearch} className="h-10 px-5 gap-2 ml-auto">
          <SearchIcon className="h-4 w-4" />
          Run checklist
        </Button>
      </div>
    </Card>
  );

  const exportRows = useMemo(
    () => (data?.items ?? []).map((item) => ({ ...item })),
    [data],
  );

  const exportColumns = useMemo(
    () => [
      { key: 'label', header: 'Check' },
      { key: 'status', header: 'Status' },
      { key: 'count', header: 'Count' },
    ],
    [],
  );

  return (
    <AccountingReportShell
      toolbar={toolbar}
      run={f.run}
      loading={isLoading}
      rows={exportRows}
      columns={exportColumns}
      entityName="Period Close Checklist"
      exportFileBase="period-close-checklist"
      exportDateTo={f.dateTo}
      statusBadge={
        f.run ? (
          <Badge variant="info" className="font-normal">
            {formatDateGlobal(f.dateFrom)} – {formatDateGlobal(f.dateTo)}
          </Badge>
        ) : undefined
      }
      recordCount={exportRows.length}
      readyTitle="Ready to run"
      readyMessage="Select period and run the close checklist."
      loadingMessage="Running checklist…"
    >
      {data && (
        <div className="p-4 grid gap-3 sm:grid-cols-2">
          {data.items.map((item) => (
            <Card key={item.code} className={cn('p-4 flex flex-col gap-2', colors.border)}>
              <div className="flex items-start justify-between gap-2">
                <p className="font-medium text-sm">{item.label}</p>
                <Badge variant={statusVariant(item.status)}>{item.status}</Badge>
              </div>
              <p className={cn('text-2xl font-semibold tabular-nums', colors.text)}>{item.count}</p>
              {item.detailUrl && item.count > 0 && (
                <Link to={item.detailUrl} className="text-xs text-indigo-600 hover:underline">
                  View details
                </Link>
              )}
            </Card>
          ))}
        </div>
      )}
    </AccountingReportShell>
  );
};
